import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  parseApiError,
  type Competition,
  type CompetitionRequest,
  type CreateCompetitionRequestInput,
} from '../lib/api';
import { cn } from '../lib/cn';
import { isAdminRole } from '../lib/roles';
import { usePageActions } from '../lib/pageActions';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { GuideMeLauncher } from '../components/GuideMeLauncher';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { CreditCard, Plus, ShieldCheck, Building2, Swords, Shield, Rocket, ChevronRight } from 'lucide-react';

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function CompetitionRequestForm({
  orgId,
  onDone,
}: {
  orgId: string;
  onDone: () => void;
}) {
  const [form, setForm] = useState<CreateCompetitionRequestInput>({
    name: '',
    teamCountEstimate: 8,
    teamSize: 4,
    projectedAdminCount: 2,
    visibility: 'PUBLIC',
  });
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: (input: CreateCompetitionRequestInput) => api.createOrgCompetitionRequest(orgId, input),
    onSuccess: () => {
      setError('');
      setForm({ name: '', teamCountEstimate: 8, teamSize: 4, projectedAdminCount: 2, visibility: 'PUBLIC' });
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Global bottom action-bar registration — the SAME "Create Quote" submit
  // as the button below (same disabled gate, same mut.mutate(form) call).
  // This component only mounts once an org is selected (Organizations' own
  // render gate below) — no separate role check needed here (see
  // lib/pageActions.tsx's role-awareness contract).
  const createQuoteActions = useMemo(
    () => ([{
      id: 'org-create-quote',
      label: 'Create quote',
      icon: CreditCard,
      onClick: () => mut.mutate(form),
      disabled: !form.name.trim() || mut.isPending,
    }]),
    [form, mut.mutate, mut.isPending],
  );
  usePageActions(createQuoteActions);

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-ink-secondary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Request Competition Quote</h2>
      </div>
      <p className="mb-4 text-xs text-ink-meta">
        Request a quote to run a tournament, then pay to unlock it.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="label">Competition name</label>
          <input
            className="input w-full"
            placeholder="e.g. Winter Cup 2026"
            value={form.name}
            onChange={e => setForm(current => ({ ...current, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="label">Estimated clans</label>
          <input
            className="input w-full"
            type="number"
            min={2}
            value={form.teamCountEstimate}
            onChange={e => setForm(current => ({ ...current, teamCountEstimate: Number(e.target.value) }))}
          />
          <p className="mt-1 text-xs text-ink-meta">How many clans you expect to enter.</p>
        </div>
        <div>
          <label className="label">Team size</label>
          <select
            className="input w-full"
            value={form.teamSize}
            onChange={e => setForm(current => ({ ...current, teamSize: Number(e.target.value) }))}
          >
            {[4, 6, 8].map(size => (
              <option key={size} value={size}>{size}v{size}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-meta">Players per team.</p>
        </div>
        <div>
          <label className="label">Projected admins</label>
          <input
            className="input w-full"
            type="number"
            min={1}
            value={form.projectedAdminCount}
            onChange={e => setForm(current => ({ ...current, projectedAdminCount: Number(e.target.value) }))}
          />
          <p className="mt-1 text-xs text-ink-meta">For planning only.</p>
        </div>
        <div>
          <label className="label">Standings visibility</label>
          <select
            className="input w-full"
            value={form.visibility}
            onChange={e => setForm(current => ({ ...current, visibility: e.target.value as CreateCompetitionRequestInput['visibility'] }))}
          >
            <option value="PUBLIC">Public standings</option>
            <option value="ORG_ONLY">Org only</option>
            <option value="PRIVATE">Private</option>
          </select>
        </div>
      </div>
      <button
        className={cn('btn-primary mt-4 inline-flex items-center gap-2', MORPH)}
        disabled={!form.name.trim() || mut.isPending}
        onClick={() => mut.mutate(form)}
      >
        <Plus className="h-4 w-4" />
        {mut.isPending ? 'Quoting…' : 'Create Quote'}
      </button>
      <ErrorMsg msg={error} />
    </Card>
  );
}

function RequestList({
  orgId,
  requests,
}: {
  orgId: string;
  requests: CompetitionRequest[];
}) {
  const [error, setError] = useState('');
  const checkoutMut = useMutation({
    mutationFn: (requestId: string) => api.createCheckoutSession(orgId, requestId),
    onSuccess: data => {
      window.location.assign(data.url);
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-ink-secondary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Competition Requests</h2>
      </div>
      <div className="space-y-3">
        {requests.length === 0 ? (
          <p className="text-sm text-ink-meta">No quotes yet.</p>
        ) : requests.map(request => (
          <div key={request.sk} className="rounded-lg bg-white/[0.04] p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink-primary">{request.name}</p>
                <p className="text-xs text-ink-meta">
                  {request.teamCountEstimate} clans · {request.teamSize}v{request.teamSize} · {request.plan}
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm font-semibold text-accent-500">{formatMoney(request.quotedAmountUsdCents)}</p>
                <p className="text-xs uppercase tracking-wider text-ink-meta">{request.status}</p>
              </div>
            </div>
            <button
              className={cn('btn-primary mt-4 inline-flex items-center gap-2', MORPH)}
              onClick={() => checkoutMut.mutate(request.sk)}
              disabled={checkoutMut.isPending}
            >
              <CreditCard className="h-4 w-4" />
              {checkoutMut.isPending ? 'Opening…' : 'Open Checkout Placeholder'}
            </button>
          </div>
        ))}
      </div>
      <ErrorMsg msg={error} />
    </Card>
  );
}

function CompetitionList({ competitions }: { competitions: Competition[] }) {
  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <Swords className="h-4 w-4 text-ink-secondary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Org Competitions</h2>
      </div>
      <div className="space-y-3">
        {competitions.length === 0 ? (
          <p className="text-sm text-ink-meta">No competitions yet.</p>
        ) : competitions.map(comp => (
          <div key={comp.id} className="rounded-lg bg-white/[0.04] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink-primary">{comp.name || comp.id.replace('COMP#', '')}</p>
                <p className="text-xs text-ink-meta">{comp.teamSize}v{comp.teamSize} · {comp.status}</p>
              </div>
              <p className="text-xs text-ink-meta">{comp.stage || 'Not started'}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function Organizations() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: orgs, isLoading } = useQuery({ queryKey: ['orgs'], queryFn: api.orgs });
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  useEffect(() => {
    if (!selectedOrgId && orgs?.length) setSelectedOrgId(orgs[0].id);
    if (selectedOrgId && orgs && !orgs.some(org => org.id === selectedOrgId)) {
      setSelectedOrgId(orgs[0]?.id || '');
    }
  }, [orgs, selectedOrgId]);

  const selectedOrg = useMemo(
    () => orgs?.find(org => org.id === selectedOrgId) ?? null,
    [orgs, selectedOrgId]
  );

  const requestsQuery = useQuery({
    queryKey: ['orgRequests', selectedOrgId],
    queryFn: () => api.orgCompetitionRequests(selectedOrgId),
    enabled: Boolean(selectedOrgId),
  });
  const competitionsQuery = useQuery({
    queryKey: ['orgCompetitions', selectedOrgId],
    queryFn: () => api.orgCompetitions(selectedOrgId),
    enabled: Boolean(selectedOrgId),
  });

  const refreshOrgData = () => {
    qc.invalidateQueries({ queryKey: ['orgs'] });
    if (selectedOrgId) {
      qc.invalidateQueries({ queryKey: ['orgRequests', selectedOrgId] });
      qc.invalidateQueries({ queryKey: ['orgCompetitions', selectedOrgId] });
    }
  };

  if (isLoading) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className={cn('flex items-start justify-between gap-3', SETTLE)} style={settleDelay(0)}>
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Organizations</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Org owners and admins can quote competitions, open the Stripe placeholder checkout, and manage org-scoped access.
          </p>
        </div>
        {/* Always-on "Guide me" nudge, same component Setup uses — Phase 4a:
            no modal any more, just a "→ Next: {step}" pointer. This page no
            longer hosts the org identity/Discord/branding controls itself
            (they all moved to Setup's Organisation tab), so without an
            `onJump` it falls back to navigating to Setup with the target
            encoded on `?jump=`. Gated to founder/admin, matching the
            Sidebar's own "Run It" visibility rule for this page. */}
        {isAdminRole(me?.role) && <GuideMeLauncher orgId={selectedOrgId || undefined} track="org" />}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <div className={SETTLE} style={settleDelay(1)}>
          <Card>
            <div className="mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-ink-secondary" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Your Organizations</h2>
            </div>
            <div className="space-y-3">
              {!orgs?.length ? (
                <p className="text-sm text-ink-meta">
                  No organizations yet. Create one from{' '}
                  <Link to="/me/org" className="text-accent-500 hover:text-accent-400">My Org</Link>{' '}
                  to start the billing and competition flow.
                </p>
              ) : orgs.map(org => (
                <button
                  key={org.id}
                  onClick={() => setSelectedOrgId(org.id)}
                  className={cn(
                    'w-full rounded-lg px-4 py-3 text-left transition',
                    MORPH,
                    selectedOrgId === org.id
                      ? 'bg-white/[0.07]'
                      : 'bg-white/[0.04] hover:bg-white/[0.06]'
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className={cn('text-sm font-semibold', selectedOrgId === org.id ? 'text-accent-500' : 'text-ink-primary')}>{org.name}</p>
                      <p className="text-xs text-ink-meta">
                        {org.membershipRole || 'platform'} · {org.billing?.entitlementStatus || 'INACTIVE'}
                      </p>
                    </div>
                    <p className="text-xs text-ink-secondary">{org.billing?.plan || 'No plan'}</p>
                  </div>
                </button>
              ))}
            </div>
          </Card>
          </div>

          {selectedOrg && (
            <>
              {/* Onboarding nudge when org has no competitions */}
              {(competitionsQuery.data?.length === 0) && (
                <div className="rounded-lg bg-white/[0.04] p-4">
                  <div className="flex items-start gap-3">
                    <Rocket className="h-5 w-5 text-accent-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-ink-primary">Ready to run a tournament?</p>
                      <p className="mt-1 text-xs text-ink-secondary">
                        {selectedOrg.name} has no competitions yet. Create one to get started, or use the setup dashboard for the full walkthrough.
                      </p>
                      <Link to="/admin/setup" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-accent-500 hover:text-accent-400">
                        Tournament Setup <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              <RequestList orgId={selectedOrg.id} requests={requestsQuery.data || []} />
              <CompetitionList competitions={competitionsQuery.data || []} />
            </>
          )}
        </div>

        <div className="space-y-6">
          {selectedOrg && (
            <>
              <div className={SETTLE} style={settleDelay(2)}>
                <Card>
                  <div className="mb-3 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-ink-secondary" />
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Selected Org</h2>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-ink-primary">{selectedOrg.name}</p>
                    <p className="text-ink-secondary">Role: {selectedOrg.membershipRole || 'platform admin'}</p>
                    <p className="text-ink-secondary">Entitlement: {selectedOrg.billing?.entitlementStatus || 'INACTIVE'}</p>
                    <p className="text-ink-secondary">Plan: {selectedOrg.billing?.plan || 'CUSTOM'} / {selectedOrg.billing?.interval || 'PER_COMPETITION'}</p>
                  </div>
                </Card>
              </div>

              <CompetitionRequestForm orgId={selectedOrg.id} onDone={refreshOrgData} />
              {me?.role === 'founder' && (
                <Card>
                  <Link to="/admin/setup" className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-ink-secondary" />
                      <div>
                        <p className="text-sm font-semibold text-ink-primary transition-colors group-hover:text-accent-500">Founder settings live in Setup → Organisation</p>
                        <p className="text-xs text-ink-meta">Billing overrides, Discord binding, role policy, and enabled formats have moved into the Setup cockpit.</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-ink-meta transition-colors group-hover:text-accent-500" />
                  </Link>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
